// LocalVideoOCRPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createWorker } from "tesseract.js";
import { useNavigate, Link } from "react-router-dom";
import storage from "../utils/local_storage";
import { addResult } from "../utils/localDatabase";
import apiClient from "../utils/apiClient";
import CREDIT_VALUE from "../utils/constants";
import { useUser } from '../utils/UserProvider'; // Adjust the import path as necessary
import PricingPage from "../components/PricingPage";

export default function LocalVideoOCRPage() {
  const { user, fetchUserProfile, profile } = useUser();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => { canvasRef.current = document.createElement("canvas"); }, []);
  const navigate = useNavigate();
  
  const [file, setFile] = useState(null);
  const [videoURL, setVideoURL] = useState("");
  const [duration, setDuration] = useState(0);
  
  const [scanSec, setScanSec] = useState(5);       // 1–40s
  const [downscale, setDownscale] = useState(1.0); // 0.25–1.0 (1.0 = original)
  const [lang, setLang] = useState("eng");
  const [uploadID, setUploadID] = useState(null)
  
  const [showPricing, setShowPricing] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  // [{ t, text, words:[{text,bbox,conf}], thumb }]
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scanningRef = useRef(false);
  const completionRef = useRef({});
  const scanOptions = [0.1, 0.2, 0.25, 0.5, 1, 2, 3, 5, 10, 30, 60, 120, 240, 360]; // whatever values you want

  // --- lifecycle / cleanup ---
  useEffect(() => {
    return () => {
      if (videoURL) URL.revokeObjectURL(videoURL);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load duration when metadata is ready
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onLoadedMeta = () => setDuration(v.duration || 0);
    v.addEventListener("loadedmetadata", onLoadedMeta);
    return () => v.removeEventListener("loadedmetadata", onLoadedMeta);
  }, [videoURL]);

  // --- sampling plan ---
const sampleTimes = useMemo(() => {
  if (!duration || !scanSec) return [];
  const raw = [];
  for (let t = 0; t < duration; t += scanSec) raw.push(t); // strict <
  raw.push(duration);                                      // add end once
  const seen = new Set();
  return raw
    .map(t => +t.toFixed(3))            // normalize to ms-ish
    .sort((a,b) => a - b)
    .filter(t => (seen.has(t) ? false : (seen.add(t), true)));
}, [duration, scanSec]);

  const upsertResult = (item) => {
    const key = +item.t.toFixed(3);
    setResults(prev => {
      const idx = prev.findIndex(r => +r.t.toFixed(3) === key);
      if (idx === -1) return [...prev, item].sort((a,b)=>a.t-b.t);
      const next = prev.slice();
      next[idx] = { ...next[idx], ...item }; // merge (fill text/words later)
      return next;
    });

  };

  // as the user scrubs the video, snap selection to nearest sampled time
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let rafId = 0;
    const onTimeUpdate = () => {
      if (scanningRef.current || !sampleTimes.length) return;
      const t = v.currentTime || 0;
      // nearest index in sampleTimes
      let bestIdx = 0, bestDiff = Infinity;
      // since sampleTimes is sorted, a small linear probe is fine; for huge lists you could binary-search
      for (let i = 0; i < sampleTimes.length; i++) {
        const d = Math.abs(sampleTimes[i] - t);
        if (d < bestDiff) { bestDiff = d; bestIdx = i; }
        else if (sampleTimes[i] > t && d > bestDiff) break; // early exit small optimization
      }
      if (bestIdx !== selectedIndex) setSelectedIndex(bestIdx);
    };

    const tick = () => { onTimeUpdate(); rafId = requestAnimationFrame(tick); };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleTimes, selectedIndex]);

  // --- file input ---
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setResults([]);
    setSelectedIndex(0);
    if (!f) {
      setFile(null);
      setVideoURL("");
      setDuration(0);
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    if (videoURL) URL.revokeObjectURL(videoURL);
    setVideoURL(url);
  };

  // --- pretty helpers ---
  const prettyTime = (s) => {
    if (!Number.isFinite(s)) return "0:00";

    const totalMs = Math.round(s * 1000);
    const mm = Math.floor(totalMs / 60000);
    const ss = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;

    // Only show ms if not an even second
    const msStr = ms === 0 ? "" : `.${ms.toString().padStart(3, "0")}`;

    return `${mm}:${ss.toString().padStart(2, "0")}${msStr}`;
  };

  const selectedText = results[selectedIndex]?.text || "";

  // --- robust frame capture ---


    function OCROverlay({ videoRef, frame, drawLines = false }) {
  if (!frame || !videoRef?.current) return null;

  const video = videoRef.current;

  // Element box (CSS pixels)
  const elW = video.clientWidth || video.getBoundingClientRect().width || 0;
  const elH = video.clientHeight || video.getBoundingClientRect().height || 0;

  // Intrinsic video content size (pixels in the source)
  const srcW = video.videoWidth || 0;
  const srcH = video.videoHeight || 0;
  if (!elW || !elH || !srcW || !srcH) return null;

  // How the browser fits the video into the element (like object-fit: contain)
  const scale = Math.min(elW / srcW, elH / srcH);
  const contentW = srcW * scale;
  const contentH = srcH * scale;

  // Letterbox offsets (black bars) inside the element
  const offX = (elW - contentW) / 2;
  const offY = (elH - contentH) / 2;

  // Helper to place a normalized box (0..1) into the content rect
  const place = (nbox) => {
    const x = offX + nbox.x * contentW;
    const y = offY + nbox.y * contentH;
    const w = nbox.w * contentW;
    const h = nbox.h * contentH;
    return { x, y, w, h };
  };

  const WORD_LIMIT = 800; // cap for perf

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {(frame.words ?? []).slice(0, WORD_LIMIT).map((w, i) => {
        if (!w?.nbox) return null;
        const { x, y, w: ww, h: hh } = place(w.nbox);
        if (ww < 2 || hh < 2) return null; // show even tiny text; adjust if too busy
        return (
          <div
            key={`w-${i}`}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: ww,
              height: hh,
              border: "2px solid rgba(255,255,0,0.9)",
              background: "rgba(255,255,0,0.12)",
              boxSizing: "border-box",
            }}
            title={w.text}
          />
        );
      })}

      {drawLines && (frame.lines ?? []).map((ln, i) => {
        if (!ln?.nbox) return null;
        const { x, y, w, h } = place(ln.nbox);
        if (w < 4 || h < 4) return null;
        return (
          <div
            key={`l-${i}`}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: w,
              height: h,
              border: "2px dashed rgba(0,200,255,0.9)",
              boxSizing: "border-box",
            }}
            title={ln.text}
          />
        );
      })}
    </div>
  );
}

const TIMEOUT_MS = 5000;

const waitFirstFrame = (video) =>
  new Promise((resolve, reject) => {
    if (video.readyState >= 2 && video.videoWidth && video.videoHeight) { resolve(); return; }
    const onLoadedData = () => { cleanup(); resolve(); };
    const onError = () => { cleanup(); reject(new Error("Video failed to load first frame")); };
    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener("loadeddata", onLoadedData);
      video.removeEventListener("error", onError);
    };
    const timer = setTimeout(() => { cleanup(); reject(new Error("Timeout waiting for first frame")); }, TIMEOUT_MS);
    video.addEventListener("loadeddata", onLoadedData, { once: true });
    video.addEventListener("error", onError, { once: true });
  });

const waitPaint = (video) =>
  new Promise((resolve) => {
    if (typeof video.requestVideoFrameCallback === "function") {
      let to = setTimeout(resolve, 1000);
      video.requestVideoFrameCallback(() => { clearTimeout(to); resolve(); });
    } else {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    }
  });

  async function createWorkerPool({ lang = "eng", size = 2, logger = () => {} } = {}) {
  size = Math.max(1, Math.min(size, (navigator.hardwareConcurrency || 4) - 1));
  const make = () => createWorker(lang, 1, { logger });
  const workers = await Promise.all(Array.from({ length: size }, () => make()));
  let next = 0;

  async function recognize(blob) {
    const i = next;
    next = (next + 1) % workers.length;
    return workers[i].recognize(blob, {}, { blocks: true });
  }
  async function setParameters(params) {
    await Promise.all(workers.map(w => w.setParameters(params)));
  }
  async function terminate() { await Promise.all(workers.map(w => w.terminate())); }

  return { recognize, setParameters, terminate, size: workers.length };
}

const grabFrameAt = (t) =>
  new Promise(async (resolve, reject) => {
    const v = videoRef.current;
    if (!v) return reject(new Error("No video element."));
    try {
      await waitFirstFrame(v);

      const draw = async () => {
        await waitPaint(v);
        const vw = v.videoWidth || 0, vh = v.videoHeight || 0;
        if (!vw || !vh) throw new Error(`Video has no dimensions (vw=${vw}, vh=${vh})`);

        const scale = Math.max(0.1, Math.min(1, downscale));
        const cw = Math.max(1, Math.floor(vw * scale));
        const ch = Math.max(1, Math.floor(vh * scale));

        const c = canvasRef.current;
        if (!c) throw new Error("Canvas not initialized");
        c.width = cw; c.height = ch;

        const ctx = c.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("Canvas 2D context unavailable");
        ctx.drawImage(v, 0, 0, cw, ch);
        ctx.filter = "contrast(200%) grayscale()";
        ctx.getImageData(0, 0, 1, 1); // probe
        resolve(c);
      };

      if (Math.abs((v.currentTime ?? 0) - t) < 0.001) { await draw(); return; }

      const onSeeked = async () => { cleanup(); try { await draw(); } catch (e) { reject(e); } };
      const onError = () => { cleanup(); reject(new Error("Video error during seek")); };
      const cleanup = () => {
        clearTimeout(timer);
        v.removeEventListener("seeked", onSeeked);
        v.removeEventListener("error", onError);
      };
      const timer = setTimeout(() => { cleanup(); reject(new Error(`Seek timeout at t=${t.toFixed(2)}s`)); }, TIMEOUT_MS);

      v.addEventListener("seeked", onSeeked, { once: true });
      if (typeof v.fastSeek === "function") {
        try { v.fastSeek(Math.min(Math.max(t, 0), duration)); }
        catch { v.currentTime = Math.min(Math.max(t, 0), duration); }
      } else {
        v.currentTime = Math.min(Math.max(t, 0), duration);
      }
    } catch (err) {
      reject(err);
    }
  });

  useEffect(() => {
    if (!sampleTimes.length) {
      setProgress(0);
      return;
    }
    const completed = results.filter(r => r.text && r.text.length > 0).length;
    setProgress(Math.round((completed / sampleTimes.length) * 100));
  }, [results, sampleTimes]);

const canvasToPngBlob = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png")
  );


    // Create a small pool of tesseract workers and a queue
    async function createWorkerPool({ lang = "eng", size = 2, logger = () => {} } = {}) {
    size = Math.max(1, Math.min(size, (navigator.hardwareConcurrency || 4) - 1));
    const make = () => createWorker(lang, 1, { logger });

    const workers = await Promise.all(Array.from({ length: size }, () => make()));

    let next = 0;
    async function recognize(blob) {
        const i = next;
        next = (next + 1) % workers.length;
        // Optional: set PSM/whitelist once per worker (do this after creation if needed)
        // await workers[i].setParameters({ tessedit_pageseg_mode: 6 });
        return workers[i].recognize(blob, {}, { blocks: true });
    }

    async function terminate() {
        await Promise.all(workers.map(w => w.terminate()));
    }

    // Set parameters on all workers (once) if you want:
    async function setParameters(params) {
        await Promise.all(workers.map(w => w.setParameters(params)));
    }

    return { recognize, terminate, setParameters, size: workers.length };
    }


     // --- main OCR flow (worker via CDN; clean & resilient) ---
      const BATCH_SIZE = 3; // Number of frames to process at once

      const runOCR = async () => {
        if (!file || !videoURL || !duration || !sampleTimes.length) return;
        setResults([])
        setUploadID(`local-${crypto.randomUUID()}`)
        setRunning(true);
        scanningRef.current = true;
        setProgress(0);
        const v = videoRef.current;
        if (v) { v.pause(); v.controls = false; v.muted = true; }

        try {
          // Spin up a small worker pool
          const pool = await createWorkerPool({
            lang,
            size: Math.min(BATCH_SIZE, Math.max(1, (navigator.hardwareConcurrency || 4) - 1)),
            tessedit_ocr_engine_mode: 1,

            // logger: (m) => console.log("[tesseract]", m),
          });

          await pool.setParameters({
            tessedit_pageseg_mode: 10,
            min_char_conf: 50
          });

          let framesCaptured = 0;
          let framesProcessed = 0;
          let completionMap = {}; 

          for (let batchStart = 0; batchStart < sampleTimes.length; batchStart += BATCH_SIZE) {
            const batchTimes = sampleTimes.slice(batchStart, batchStart + BATCH_SIZE);

            // 1) Capture frames for this batch
            const batchTasks = [];
            for (let i = 0; i < batchTimes.length; i++) {
              const t = batchTimes[i];
              try {
                const c = await grabFrameAt(t);
                const thumb = c.toDataURL("image/jpeg", 0.7);
                const blob = await canvasToPngBlob(c);

                upsertResult({ t, text: "", words: [], thumb, ocrW: c.width, ocrH: c.height });
                batchTasks.push({ t, blob, thumb, ocrW: c.width, ocrH: c.height });
              } catch (e) {
                console.error("Capture failed @", t, e);
                upsertResult({ t, text: "", words: [], thumb: "", ocrW: 0, ocrH: 0 });
              }
              framesCaptured += 1;
              // Progress: 0-50% for capture, 50-100% for OCR
              // setProgress(Math.round((framesCaptured / sampleTimes.length) * 50));
              await new Promise(r => setTimeout(r, 0));
            }

            // 2) OCR in parallel for this batch
            await Promise.all(
              batchTasks.map(async ({ t, blob, thumb, ocrW, ocrH }) => {
                if (!blob) return;
                try {
                  const { data } = await pool.recognize(blob);

                  const lines = [];
                  let words = [];
                  const MIN_CONF = 80;

                  for (const b of data.blocks ?? []) {
                    for (const p of b.paragraphs ?? []) {
                      for (const l of p.lines ?? []) {
                        // Filter words in this line by confidence
                        const filteredWords = (l.words ?? []).filter(
                          (w) => w?.bbox && w.confidence >= MIN_CONF && w.text.trim().length > 0
                        );

                        if (filteredWords.length === 0) continue; // skip line if no high-conf words

                        // Add filtered words to the words array
                        words.push(
                          ...filteredWords.map((w) => {
                            const { x0, y0, x1, y1 } = w.bbox;
                            return {
                              text: w.text || "",
                              nbox: {
                                x: x0 / ocrW,
                                y: y0 / ocrH,
                                w: (x1 - x0) / ocrW,
                                h: (y1 - y0) / ocrH,
                              },
                              conf: w.confidence,
                            };
                          })
                        );

                        // Add line text constructed from filtered words only
                        lines.push(filteredWords.map((w) => w.text).join(" ").trim());
                      }
                    }
                  }

                  const text = lines.join("\n");
                  upsertResult({ t, text, words, thumb, ocrW, ocrH });
                } catch (e) {
                  console.error("OCR failed @", t, e);
                  upsertResult({ t, text: "", words: [], thumb, ocrW, ocrH });
                } finally {
                  // optional: update progress here
                }
              })
            );


            // Release blobs for this batch
            batchTasks.length = 0;
          }

          await pool.terminate();
          // deduct credits
          logLocalUpload()
        } catch (e) {
          console.error("OCR fatal:", e);
          alert(e?.message || "OCR failed");
        } finally {
          scanningRef.current = false;
          setRunning(false);
          if (v) v.controls = true;
        }
      }

      async function logLocalUpload() {

        const fileName = file?.name || "Local File"
        const ocrType = "local"
        const mediaContent = "numerical"
        const uploadName = file?.name || "Local Upload"
        const targetFrameRate = scanSec

        try {
          const response = await apiClient.post("/logLocalUpload", {
              uploadName,
              mediaContent,
              duration,
              targetFrameRate,
              fileName,
              ocrType,
              uploadID
          });

          return
        } catch (err) {
          console.error("Failed to log local upload:", err);
          throw err;
        }
      }



  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="w-full flex flex-col mb-4 items-center align-center ">
        <div className="w-full flex flex-row items-center justify-between align-center ">
          <h2 className="text-2xl font-bold text-center mx-auto">Local Text Extraction</h2>
        {/* <p className="text-md">Video and text analysis will only be saved if .</p> */}
        </div>
        <h2 className="text-lg text-center mx-auto">We suggest analyzing 3-4 key frames first to confirm the speed and quality of results. </h2>
        <h2 className="text-lg text-center mx-auto">If not, you can try our cloud based text extraction processing options <a onClick={()=>navigate('/upload')} className="link">here</a>.</h2>
      </div>
      {/* Controls */}
      <div className="card bg-base-200 shadow mb-4">
        <div className="card-body p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label"><span className="label-text font-bold">Select Video</span></label>
            <input type="file" accept="video/*" className="file-input file-input-bordered w-full" onChange={handleFileChange} />

            <div className="mt-4">
              <label className="label">
                <span className="label-text font-bold">Scan every {scanSec} seconds</span>
                <span className="label-text font-bold">Required Credits: {Math.ceil(duration/scanSec*CREDIT_VALUE["local"])}</span>
              </label>
              {profile?.credits < Math.ceil(duration/scanSec*CREDIT_VALUE["local"]) && (
                    <div className="flex flex-col mx-auto text-error text-center">
                      You have {profile?.credits} credits but this operation requires {Math.ceil(duration/scanSec*CREDIT_VALUE["local"])}.
                      <button className="btn btn-sm btn-primary m-2 mx-auto" onClick={() => setShowPricing(true)}>Purchase Credits</button>
                    </div>
                  )}
              <input
                type="range"
                min={0}
                max={scanOptions.length - 1}
                step={1}
                value={scanOptions.indexOf(scanSec)}
                className="range range-primary"
                onChange={(e) => setScanSec(scanOptions[parseInt(e.target.value, 10)])}
              />
              <div className="text-sm opacity-70">
                ~{sampleTimes.length} frames
              </div>
            </div>

            <div className="mt-4">
              <label className="label"><span className="label-text font-bold">Downscale for OCR</span></label>
              <input
                type="range"
                min={0.8} max={1} step={0.05} value={downscale}
                className="range range-secondary"
                onChange={(e) => setDownscale(parseFloat(e.target.value))}
              />
              <div className="text-sm opacity-70">Scale: {(downscale * 100).toFixed(0)}%</div>
            </div>

            {false && <div className="mt-4">
              <label className="label"><span className="label-text font-bold">Language</span></label>
              <select
                className="select select-bordered w-full max-w-xs"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
              >
                <option value="eng">English (eng)</option>
                {/* Add more traineddata to your app and extend options */}
              </select>
            </div>}
            {sampleTimes?.length > 200 && <p className="bg-red-500 text-white p-2 rounded-md">Whoa that's a lot of frames. For the sake of your computer we suggest reducing your framerate or splitting the upload media into multiple parts.</p>}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className={`btn btn-primary ${running || !file || !duration || sampleTimes.length === 0 ? "btn-disabled" : ""}`}
                onClick={runOCR}
                disabled={running || !file || !duration || sampleTimes.length === 0 || sampleTimes?.length > 200 || profile.credits < Math.ceil(duration/scanSec*CREDIT_VALUE["local"])}
              >
                {running ? "Scanning…" : "Run OCR"}
              </button>
              {running && (
                <div className="radial-progress" style={{ "--value": progress }} role="progressbar">
                  {progress}%
                </div>
              )}
              <button
                className="btn btn-secondary btn-outline"
                disabled={!results.length || running}
                onClick={() => {
                  const all = results
                    .map((r, i) => `Frame ${i + 1} (${prettyTime(r.t)})\n-------------------\n${r.text || ""}\n`)
                    .join("\n");
                  const payload = "\uFEFF" + all;
                  const blob = new Blob([payload], { type: "text/plain;charset=utf-8" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = "video-ocr.txt";
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                Download Text
              </button>
              <button
                className="btn btn-secondary btn-outline"
                disabled={!results.length || running}
                onClick={async () => {
                  await addResult({ id: uploadID, data: results, uploadedAt: Math.floor(new Date().getTime() / 1000), duration, targetFrameRate:scanSec, fileName: file.name });
                  console.log("added to database");
                  navigate(`/view-upload/${uploadID}`);
                }}
              >
                Save and Analyze
              </button>
              {/* <button
                className="btn btn-sm"
                disabled={!selectedText}
                onClick={() => {
                  const payload = "\uFEFF" + (selectedText || "");
                  const blob = new Blob([payload], { type: "text/plain;charset=utf-8" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `frame-${selectedIndex + 1}.txt`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                Download Current Frame Text
              </button> */}
            </div>
          </div>

          {/* Video Preview */}
          <div>
            <label className="label"><span className="label-text font-bold">Preview</span></label>
            <div className="rounded-lg border overflow-hidden">
            {videoURL ? (
                <div style={{ position: "relative" }}>
                <video
                    ref={videoRef}
                    src={videoURL}
                    controls={!running}
                    playsInline
                    preload="metadata"
                    className="w-full max-h-[50vh] bg-black"
                    style={{ pointerEvents: running ? "none" : "auto", opacity: running ? 0.6 : 1 }}
                />
                {/* Overlay on top of the video */}
                <OCROverlay videoRef={videoRef} frame={results[selectedIndex]} />
                </div>
            ) : (
                <div className="p-6 text-sm opacity-70">Choose a video file to begin.</div>
            )}
            </div>
{/* Frame navigation buttons */}
      <div className="flex justify-center items-center gap-4 mt-2">
        <button
          className="btn btn-sm btn-outline"
          disabled={selectedIndex <= 0 || scanningRef.current}
          onClick={() => {
            if (selectedIndex > 0) {
              setSelectedIndex(selectedIndex - 1);
              const v = videoRef.current;
              if (v) v.currentTime = results[selectedIndex - 1]?.t ?? 0;
            }
          }}
        >
          Previous
        </button>
        <span className="text-sm opacity-70">
          Frame {selectedIndex + 1} / {results.length}
        </span>
        <button
          className="btn btn-sm btn-outline"
          disabled={selectedIndex >= results.length - 1 || scanningRef.current}
          onClick={() => {
            if (selectedIndex < results.length - 1) {
              setSelectedIndex(selectedIndex + 1);
              const v = videoRef.current;
              if (v) v.currentTime = results[selectedIndex + 1]?.t ?? 0;
            }
          }}
        >
          Next
        </button>
      </div>
            {duration > 0 && (
              <div className="text-xs opacity-70 mt-2">
                Duration: {prettyTime(duration)} • Sampling {sampleTimes.length} frames
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: frames list */}
        <div className="lg:col-span-1">
          <div className="rounded-lg border max-h-[70vh] overflow-auto">
            {!results.length ? (
              <div className="p-4 text-sm opacity-70">No OCR results yet. Click “Run OCR”.</div>
            ) : (
              <ul className="menu">
                {sampleTimes.map((t, i) => {
                  const r = results.find(res => Math.abs(res.t - t) < 0.01) || {};
                  return (
                    <li key={i} className="p-2">
                      <button
                        className={`w-full text-left ${i === selectedIndex ? "bg-base-200 rounded" : ""}`}
                        onClick={() => {
                          // if (scanningRef.current) return;
                          setSelectedIndex(i);
                          const v = videoRef.current;
                          if (v) v.currentTime = t;
                        }}
                        title={`Frame at ${prettyTime(t)}`}
                      >
                        <div className="flex items-center gap-3">
                          {r.thumb ? (
                            <img
                              src={r.thumb}
                              alt={`frame ${i + 1}`}
                              className="w-28 h-20 object-cover rounded border"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-28 h-20 rounded border bg-base-200 grid place-items-center text-xs opacity-60">
                              {running ? <span className="loading loading-spinner loading-xs"></span> : "no image"}
                            </div>
                          )}
                          <div className="text-xs opacity-80">
                            <div>Frame {i + 1}</div>
                            <div>{prettyTime(t)}</div>
                            {!r.text && running && (
                              <div className="opacity-60 text-xs flex items-center gap-1">
                                <span>Processing…</span>
                                {/* <span className="loading loading-spinner loading-xs"></span> */}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Right: text */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border p-4 h-[70vh] overflow-auto whitespace-pre-wrap">
            {sampleTimes.length === 0 ? (
              <div className="opacity-70">No extracted text to display.</div>
            ) : (() => {
              const t = sampleTimes[selectedIndex];
              const r = results.find(res => Math.abs(res.t - t) < 0.01) || {};
              if (r.text) {
                return (
                  <>
                    <div className="badge badge-outline mb-2">
                      Frame {selectedIndex + 1} • {prettyTime(t)}
                    </div>
                    <div className="prose max-w-none text-sm">{r.text}</div>
                  </>
                );
              }
              return (
                running ? 
                  <div className="opacity-70 flex items-center gap-2">
                    <span>Processing OCR for this frame…</span>
                    <span className="loading loading-spinner loading-xs"></span>
                  </div>
                  :
                  <div>
                    <p className = "p-4 text-sm opacity-70"> No OCR results yet. Click “Run OCR”.</p>
                    </div>
              );
            })()}
          </div>

          
        </div>
      </div>

      {showPricing && (
        <div className="modal modal-open wax-w-full">
          <div className="modal-box flex flex-col items-center gap-2 w-11/12 max-w-full ">
          <div className="flex flex-col items-center gap-4">
            <p className="text-2xl font-bold">Purchase more credits</p>
            <p className="italic text-sm text-center">Credits can be mixed and matched across processing types.</p>
            <div className="stats shadow">
              <div className="stat">
                <div className="stat-title">Credit Balance:</div>
                <div className="stat-value">{profile?.credits || 0}</div>
                <div className="stat-desc">credits</div>
              </div>
            </div>
          </div>
            <PricingPage/>
            <div className="modal-action w-full text-right">
              <button className="btn" onClick={() => setShowPricing(false)}>Close</button>
            </div>
          </div>
        </div>)}
    </div>
  );
}
